import { useEffect, useRef, useState } from 'react';
import { useCMS } from '@/hooks/useCMS';

const DEFAULT = {
  label: 'პორტფოლიო',
  heading: 'დასრულებული პროექტები',
  categories: ['ყველა', 'კომერციული', 'სასტუმრო', 'საცხოვრებელი'],
  guarantee_label: 'გარანტიით დასრულებული',
  stats: [
    { value: '5,000+', label: 'დასრულებული პროექტი' },
    { value: '67,500', label: 'მ² მომარაგებული ფართობი' },
    { value: '50+',    label: 'პრემიუმ ბრენდი' },
    { value: '25+',    label: 'წლის გამოცდილება' },
  ],
  projects: [
    { name: 'TBC Business Center', location: 'თბილისი, საქართველო', area: '12,000 მ²', year: '2023', materials: 'Laminam, Antolini, Hansgrohe', category: 'კომერციული', image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20business%20center%20interior%20lobby%20with%20large%20format%20porcelain%20tiles%20and%20marble%20surfaces%2C%20high%20ceilings%2C%20elegant%20reception%20area%2C%20warm%20ambient%20lighting%2C%20premium%20construction%20materials%2C%20sophisticated%20neutral%20tones&width=800&height=600&seq=proj1&orientation=landscape' },
    { name: 'Stamba Hotel',        location: 'თბილისი, საქართველო', area: '8,500 მ²',  year: '2022', materials: 'Laminam, Antolini, Ardex',       category: 'სასტუმრო',    image: 'https://readdy.ai/api/search-image?query=luxury%20boutique%20hotel%20interior%20with%20premium%20stone%20flooring%20and%20elegant%20marble%20surfaces%2C%20warm%20ambient%20lighting%2C%20sophisticated%20design%2C%20high-end%20construction%20materials%2C%20neutral%20warm%20tones&width=800&height=600&seq=proj2&orientation=landscape' },
    { name: 'M2 Residence',        location: 'თბილისი, საქართველო', area: '3,200 მ²',  year: '2024', materials: 'Laminam, Dicson, Hansgrohe',     category: 'საცხოვრებელი', image: 'https://readdy.ai/api/search-image?query=luxury%20modern%20apartment%20building%20interior%20lobby%20with%20premium%20flooring%20and%20elegant%20surfaces%2C%20warm%20ambient%20lighting%2C%20sophisticated%20residential%20design%2C%20high-end%20materials%2C%20neutral%20warm%20tones&width=800&height=600&seq=proj3&orientation=landscape' },
    { name: 'Galleria Tbilisi',    location: 'თბილისი, საქართველო', area: '15,000 მ²', year: '2021', materials: 'Laminam, Ardex, Tecnopol',       category: 'კომერციული',  image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20shopping%20mall%20interior%20with%20large%20format%20tiles%20and%20premium%20flooring%2C%20bright%20elegant%20design%2C%20high-end%20commercial%20construction%20materials%2C%20sophisticated%20neutral%20tones&width=800&height=600&seq=proj4&orientation=landscape' },
    { name: 'Radisson Blu',        location: 'ბათუმი, საქართველო',  area: '22,000 მ²', year: '2020', materials: 'Laminam, Antolini, Hansgrohe',  category: 'სასტუმრო',    image: 'https://readdy.ai/api/search-image?query=luxury%20hotel%20interior%20with%20premium%20marble%20flooring%20and%20elegant%20stone%20surfaces%2C%20warm%20ambient%20lighting%2C%20sophisticated%20hospitality%20design%2C%20high-end%20construction%20materials%2C%20neutral%20warm%20tones&width=800&height=600&seq=proj5&orientation=landscape' },
    { name: 'Vake Park Residences', location: 'თბილისი, საქართველო', area: '6,800 მ²', year: '2023', materials: 'Laminam, Dicson, Ardex',         category: 'საცხოვრებელი', image: 'https://readdy.ai/api/search-image?query=luxury%20residential%20building%20interior%20with%20premium%20flooring%20and%20elegant%20surfaces%2C%20warm%20ambient%20lighting%2C%20sophisticated%20apartment%20design%2C%20high-end%20materials%2C%20neutral%20warm%20tones&width=800&height=600&seq=proj6&orientation=landscape' },
  ],
};

export default function ProjectsShowcase() {
  const cms = useCMS('projects_showcase', DEFAULT);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState('ყველა');

  const allCats: string[] = cms.categories ?? DEFAULT.categories;
  const projects = cms.projects ?? DEFAULT.projects;
  const filtered = filter === allCats[0] ? projects : projects.filter((p: { category: string }) => p.category === filter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-[#F7F4F1] py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="reveal opacity-0 flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-phoenix-red"></div>
              <span className="text-phoenix-red text-xs font-semibold tracking-[0.3em] uppercase">{cms.label}</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-phoenix-dark leading-tight">{cms.heading}</h2>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="reveal opacity-0 flex flex-wrap gap-2 mb-12">
          {allCats.map((cat: string) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                filter === cat
                  ? 'bg-phoenix-red text-white'
                  : 'bg-white text-phoenix-dark/60 hover:text-phoenix-dark border border-phoenix-cream-dark'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project: typeof DEFAULT.projects[0], i: number) => (
            <div
              key={project.name}
              className="reveal opacity-0 group bg-white rounded-sm overflow-hidden cursor-pointer"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={project.image} alt={project.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-phoenix-dark text-xs font-semibold px-3 py-1.5 rounded-sm">{project.category}</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-serif text-xl font-bold text-white mb-1">{project.name}</h3>
                  <div className="flex items-center gap-3 text-white/70 text-xs">
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{project.location}</span>
                    <span className="flex items-center gap-1"><i className="ri-ruler-line"></i>{project.area}</span>
                  </div>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-phoenix-gray">{project.year}</span>
                  <span className="text-xs text-phoenix-red font-medium">{project.materials}</span>
                </div>
                <div className="h-px bg-phoenix-cream-dark mb-3"></div>
                <div className="flex items-center gap-2 text-xs text-phoenix-dark/60">
                  <i className="ri-shield-check-line text-phoenix-red"></i>
                  <span>{cms.guarantee_label}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom stats */}
        <div className="reveal opacity-0 grid grid-cols-2 md:grid-cols-4 gap-px bg-phoenix-cream-dark mt-16 rounded-sm overflow-hidden">
          {(cms.stats ?? DEFAULT.stats).map((stat: { value: string; label: string }, i: number) => (
            <div key={i} className="bg-white p-6 md:p-8 text-center">
              <div className="font-display text-2xl md:text-3xl font-bold text-phoenix-red mb-1">{stat.value}</div>
              <div className="text-xs text-phoenix-gray">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
